import { ObjectId } from 'bson';
import { computed, makeObservable, toJS } from 'mobx';

import { concatPath, moment, TimeFormat } from '@feathr/hooks';
import type { Languages } from '@feathr/locales';
import type { DeepObservable, IBaseAttributes, TConstraints } from '@feathr/rachis';
import { Collection, DisplayModel } from '@feathr/rachis';

import type { TCampaignGroup } from './campaigns';
import { CampaignClass } from './campaigns';
import type { TFlagsRecord } from './flags';
import type { IConfig } from './redirects';

const reportsClassNames = [...Object.values(CampaignClass), 'Flight', 'Event'] as const;
export type TReportClass = (typeof reportsClassNames)[number];

export interface IUserSettings {
  displayed_campaign_columns?: Record<TCampaignGroup, string[]>;
  displayed_campaign_columns_global?: Record<TCampaignGroup, string[]>;
  persons_column_ids?: string[];
  favorite_account_ids?: string[];
  invite_partners_column_ids?: string[];
  partners_column_ids?: string[];
  segments_column_ids?: string[];
  reports_config?: Record<TReportClass, IConfig>;
  lng?: Languages;
}

export type TUserRole = 'csm' | 'superuser';

export interface IUser extends IBaseAttributes {
  account: string;
  calendly_link?: string;
  confirmed: boolean;
  date_last_access: string;
  date_last_modified: string;
  email: string;
  flags: TFlagsRecord;
  last_nps: number | null;
  login_attempts: number;
  /** Total number of logins  */
  logins: number;
  name?: string;
  picture?: string;
  recent_account_ids: string[];
  role?: TUserRole;
  settings: IUserSettings;
  skilljar_id: string;
  timezone: string;
  username?: string;
  email_opt_out?: boolean;
}

export class User extends DisplayModel<IUser> {
  public readonly className = 'User';

  public constraints: TConstraints<IUser> = {
    name: {
      presence: {
        allowEmpty: false,
      },
    },
    timezone: {
      presence: {
        allowEmpty: false,
      },
    },
  };

  constructor(attributes: Partial<IUser> = {}) {
    super(attributes);

    makeObservable(this);
  }

  public getItemUrl(pathSuffix?: string): string {
    const currentUserId = '';
    if (this.id === currentUserId) {
      return concatPath(`/settings/user/profile`, pathSuffix);
    }
    return concatPath(`/settings/account/users/${this.id}`, pathSuffix);
  }

  public getDefaults(): Partial<IUser> {
    return {
      recent_account_ids: [],
    };
  }

  /**
   * Get reports_config setting - maps report classes to report configs
   */
  public getReportsConfigSetting(
    reportClass: TReportClass,
    initialConfig: IConfig,
  ): Record<TReportClass, IConfig> {
    return this.getSetting('reports_config', {
      [reportClass]: initialConfig,
    } as Record<TReportClass, IConfig>);
  }

  /**
   * Set reports_config setting (forcing settings attribute to be dirty)
   */
  public setReportsConfigSetting(config: Record<TReportClass, IConfig>): void {
    this.setSetting('reports_config', config);
    this.setAttributeDirty('settings');
  }

  public getSetting<K extends keyof IUserSettings & string>(
    attribute: K,
  ): DeepObservable<IUserSettings[K]>;

  public getSetting<K extends keyof IUserSettings & string>(
    attribute: K,
    defaultValue: Exclude<IUserSettings[K], undefined>,
  ): DeepObservable<Exclude<IUserSettings[K], undefined>>;

  public getSetting<K extends keyof IUserSettings & string>(
    key: K,
    defaultValue?: unknown,
  ): unknown {
    const settings: IUserSettings = this.get('settings', {});
    return settings[key] !== undefined && settings[key] !== null ? settings[key] : defaultValue;
  }

  public setSetting<K extends keyof IUserSettings & string>(key: K, value: IUserSettings[K]): void {
    const settings: IUserSettings = this.get('settings', {});
    this.set({ settings: { ...settings, [key]: value } });
  }

  public get flags(): TFlagsRecord {
    /*
     * This getter exists because we only want to consider the truthy keys to override
     * account flags. User flags should be able to set a flag to true, not set it back
     * to false (turn off a feature) if it is already on at the account level.
     */
    if (!this.get('flags')) {
      return {};
    }
    return Object.fromEntries(
      Object.entries(toJS(this.get('flags'))).filter(([, value]) => {
        return value as boolean;
      }),
    );
  }

  @computed
  public get dateCreated(): string {
    return moment.utc(new ObjectId(this.id).getTimestamp()).format(TimeFormat.isoDateTime);
  }

  @computed
  public get isSudoer(): boolean {
    const role = this.get('role');
    return !!role && ['csm', 'superuser'].includes(role);
  }

  @computed
  public get isSuperuser(): boolean {
    return this.get('role') === 'superuser';
  }

  @computed
  public get name(): string {
    return this.get('name', '').trim() || 'Unnamed User';
  }
}

export class Users extends Collection<User> {
  public getModel(attributes: Partial<IUser>): User {
    return new User(attributes);
  }

  public getClassName(): string {
    return 'users';
  }
}
